import React, { PropsWithChildren } from 'react';
import useCategoryId from '../../../../hooks/useCategoryId';
import { useCategoryConfiguration } from '../../../../hooks/useCategoryConfiguration';
import { ConfigurationFieldType, SkuType } from '@ydistri/api-sdk';
import {
  ConditionalConfigurationCheckboxesValue,
  ConditionalConfigurationContentPredicate,
  ConditionalConfigurationContentPredicateData,
} from './conditionalConfigurationContentTypes';

interface BaseConditionalConfigurationContentProps extends PropsWithChildren {
  fieldTypes: ConfigurationFieldType[];
  skuType?: SkuType | null;
  categoryId?: number;
}

interface PredicateConditionalConfigurationContentProps extends BaseConditionalConfigurationContentProps {
  predicate: ConditionalConfigurationContentPredicate;
  renderWhenValue?: never;
}

interface CheckboxConditionalConfigurationContentProps extends BaseConditionalConfigurationContentProps {
  renderWhenValue: ConditionalConfigurationCheckboxesValue;
  predicate?: never;
}

type ConditionalConfigurationContentProps =
  | PredicateConditionalConfigurationContentProps
  | CheckboxConditionalConfigurationContentProps;

/**
 * Displays children conditionally based on configuration values.
 * For the common checkbox scenario, when ALL the fieldTypes are checkboxes, use renderWhenValue.
 * For custom conditions, provide predicate.
 * @param fieldTypes Array of fields whose values will be tested
 * @param renderWhenValue Checkbox condition mode for the common use-case
 * @param predicate Predicate function for custom condition evaluation
 * @param skuType SkuType to use to read a configuration value, if not provided or is null, FastMover will be used
 * @param categoryId Optional category id, if not provided, uses currently selected category
 * @param children Content to be rendered when value satisfies the condition
 */
const ConditionalConfigurationContent: React.FC<ConditionalConfigurationContentProps> = ({
  categoryId,
  fieldTypes,
  skuType = null,
  children,
  ...condition
}) => {
  const selectedCategoryId = useCategoryId();
  const { categoryConfigurationData } = useCategoryConfiguration(categoryId ?? selectedCategoryId);
  const effectiveSkuType = skuType ?? SkuType.FastMover;

  const predicateData = fieldTypes.map<ConditionalConfigurationContentPredicateData>(fieldType => {
    const configurationValue = categoryConfigurationData?.[fieldType][effectiveSkuType];
    return { fieldType, value: configurationValue?.value };
  });

  let shouldRender = false;
  if ('renderWhenValue' in condition) {
    const values = predicateData.map(item => item.value);
    switch (condition.renderWhenValue) {
      case ConditionalConfigurationCheckboxesValue.ALL_ON: {
        shouldRender = values.every(value => value === '1');
        break;
      }
      case ConditionalConfigurationCheckboxesValue.ALL_OFF: {
        shouldRender = values.every(value => value === '0');
        break;
      }
      case ConditionalConfigurationCheckboxesValue.AT_LEAST_ONE_ON: {
        shouldRender = values.some(value => value === '1');
        break;
      }
      case ConditionalConfigurationCheckboxesValue.AT_LEAST_ONE_OFF: {
        shouldRender = values.some(value => value === '0');
        break;
      }
      case undefined: {
        throw new Error('Not implemented yet: undefined case');
      }
    }
  } else {
    shouldRender = condition.predicate(predicateData);
  }

  if (shouldRender) {
    return children;
  } else {
    return null;
  }
};

export default ConditionalConfigurationContent;
